// Utilidades para cálculo de niveles esperados y año anterior (unificadas)

import { getCourseBaseName, getCourseOrderIndex } from './courseUtils';

// Constantes base del sistema
export const BASE_MIN_AGE = 3; // Medio Mayor
export const BASE_MAX_AGE = 12; // 6° básico esperado (12 o más)

/**
 * Conversión de edad a unidades del gráfico (escala absoluta de 20 por año) con desplazamiento +20
 *  - 3 años → 20
 *  - 4 años → 40
 *  - ...
 *  - 12 años → 200
 */
export const ageToChartUnits = (age: number): number => {
  const clamped = Math.max(BASE_MIN_AGE, Math.min(BASE_MAX_AGE, age));
  return (clamped - BASE_MIN_AGE) * 20 + 20;
};

/**
 * Offset para mover el eje X de acuerdo al minAge elegido
 */
export const chartOffsetForMinAge = (minAge: number): number => {
  const clamped = Math.max(BASE_MIN_AGE, Math.min(BASE_MAX_AGE, minAge));
  return (clamped - BASE_MIN_AGE) * 20;
};

/**
 * Obtiene edad base A a partir del curso (Medio Mayor=3, Pre Kinder=4, ..., Sexto básico=11)
 */
export const getBaseAgeForCourse = (courseName?: string | null): number | null => {
  if (!courseName) return null;
  const index = getCourseOrderIndex(courseName);
  if (index !== 999) {
    return BASE_MIN_AGE + index; // 0→3, 1→4, ... 8→11
  }
  const base = getCourseBaseName(courseName);
  const indexBase = getCourseOrderIndex(base);
  if (indexBase !== 999) {
    return BASE_MIN_AGE + indexBase;
  }
  return null;
};

/**
 * Cálculo robusto de edad desde string (acepta 'DD-MM-YYYY' o cualquier parseable por Date)
 */
export const calculateAge = (birthDate: string): number => {
  if (!birthDate) return BASE_MIN_AGE;
  let birth: Date | null = null;
  // Intentar DD-MM-YYYY
  const dmyMatch = birthDate.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) {
    const [, dd, mm, yyyy] = dmyMatch;
    birth = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  } else {
    // Fallback al parser nativo
    const parsed = new Date(birthDate);
    if (!isNaN(parsed.getTime())) birth = parsed;
  }
  if (!birth) return BASE_MIN_AGE;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return Math.max(BASE_MIN_AGE, Math.min(BASE_MAX_AGE, age));
};

/**
 * Obtiene la edad "ancla" A según preferencia (curso o edad real)
 */
export const deriveAnchoredAge = (
  params: { course?: string; birthDate?: string; preferCourse?: boolean }
): number => {
  const { course, birthDate, preferCourse = true } = params || {} as any;
  const courseAge = getBaseAgeForCourse(course ?? undefined);
  const realAge = birthDate ? calculateAge(birthDate) : null;
  if (preferCourse) {
    return (courseAge ?? realAge ?? BASE_MIN_AGE);
  }
  return (realAge ?? courseAge ?? BASE_MIN_AGE);
};

/**
 * Rango EXACTAMENTE de 6 años (A-2 ... A+3) limitado a [3, 12]
 */
export const getSixYearRangeForAge = (anchoredAge: number): { minAge: number; maxAge: number } => {
  let minAge = Math.max(BASE_MIN_AGE, anchoredAge - 2);
  let maxAge = Math.min(BASE_MAX_AGE, minAge + 5);
  return { minAge, maxAge };
};

/**
 * Genera ticks y etiquetas para el eje X dadas las edades min y max (transformadas por offset)
 */
export const createAgeTicksAndLabels = (
  minAge: number,
  maxAge: number
): { ticks: number[]; labels: { [k: number]: string } } => {
  const ticks: number[] = [];
  const labels: { [k: number]: string } = {};
  const offset = chartOffsetForMinAge(minAge);
  for (let a = minAge; a <= maxAge; a++) {
    const transformed = ageToChartUnits(a) - offset;
    ticks.push(transformed);
    labels[transformed] = `${a} años`;
  }
  return { ticks, labels };
};

/**
 * Porcentaje absoluto (en unidades del gráfico) para el NIVEL ESPERADO (A+1)
 */
export const calculateExpectedPercentage = (age: number): number => {
  // A+1 (cap en 12)
  const targetAge = Math.min(BASE_MAX_AGE, age + 1);
  return ageToChartUnits(targetAge);
};

/**
 * Porcentaje absoluto (en unidades del gráfico) para el NIVEL ANTERIOR (A)
 */
export const calculatePreviousYearPercentage = (age: number): number => {
  const targetAge = Math.max(BASE_MIN_AGE, Math.min(BASE_MAX_AGE, age));
  return ageToChartUnits(targetAge);
};

/**
 * Obtiene la edad objetivo para el nivel esperado
 */
export const getExpectedTargetAge = (currentAge: number): number => {
  return Math.min(BASE_MAX_AGE, currentAge + 1);
};

/**
 * Obtiene la edad para el nivel anterior
 */
export const getPreviousTargetAge = (currentAge: number): number => {
  return Math.max(BASE_MIN_AGE, currentAge);
};

/**
 * Mapeo de edades a cursos (3 a 11 años - 9 niveles)
 */
export const AGE_TO_COURSE_MAP: { [key: number]: string } = {
  3: 'Medio Mayor',
  4: 'Pre Kinder',
  5: 'Kinder',
  6: '1° Básico',
  7: '2° Básico',
  8: '3° Básico',
  9: '4° Básico',
  10: '5° Básico',
  11: '6° Básico',
};

/**
 * Mapeo de cursos a edades típicas (3 a 11 años - 9 niveles)
 * Incluye variaciones comunes de nombres de cursos
 */
export const COURSE_TO_AGE_MAP: { [key: string]: number } = {
  'Medio Mayor': 3,
  'Pre Kinder': 4,
  'Kinder': 5,
  '1° Básico': 6,
  '2° Básico': 7,
  '3° Básico': 8,
  '4° Básico': 9,
  '5° Básico': 10,
  '6° Básico': 11,
  // Variaciones con ordinales escritos
  'Primero básico': 6,
  'Segundo básico': 7,
  'Tercero básico': 8,
  'Cuarto básico': 9,
  'Quinto básico': 10,
  'Sexto básico': 11,
  // Variaciones con mayúsculas
  'Primero Básico': 6,
  'Segundo Básico': 7,
  'Tercero Básico': 8,
  'Cuarto Básico': 9,
  'Quinto Básico': 10,
  'Sexto Básico': 11,
};
